import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { Close } from '@material-ui/icons'

import { DrawerBodyWrapper, DrawerFooterWrapper, Loader, TOAST_TYPE } from '@base'
import { Button, Checkbox, Input } from '@baseV2'
import { MODAL_TYPES, strings } from '@constants'
import { cancelUserSubscription, editTenderUser, getUserSubscriptions } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer, toggleModal } from '@data/state/action/root'
import { Spacings, Text } from '@styles'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MAX_EMAILS = 5
const DEFAULT_MAX_COUNT = 100
const DEFAULT_SEARCH_LIMIT = 2
// Accepts a ms timestamp or an ISO string; returns a readable date or a dash.
const formatDate = (value) => {
	if (!value) return '-'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return '-'
	return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const Section = styled.div`
	margin-bottom: ${Spacings.SPACING_5B};
`

const SectionTitle = styled.h3`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
	margin-bottom: ${Spacings.SPACING_3B};
`

const CountWrapper = styled.div`
	margin-top: ${Spacings.SPACING_3B};
`

// Keeps the toggle and its limit input on the same line.
const InlineRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_4B};
`

const LimitWrapper = styled.div`
	width: 120px;
`

const EmailRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_2B} 0;
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

const EmailText = styled.span`
	flex: 1;
	min-width: 0;
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const PrimaryTag = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.brand.primary};
	background: ${({ theme }) => theme.colors.brand.subtle};
	padding: ${Spacings.SPACING_1} ${Spacings.SPACING_2B};
	border-radius: ${({ theme }) => theme.radius.sm};
	white-space: nowrap;
`

const IconButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
	color: ${({ theme }) => theme.colors.text.secondary};
	opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
	padding: ${Spacings.SPACING_1};

	& > svg {
		font-size: ${Text.LARGE};
	}
`

const AddEmailRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	margin-top: ${Spacings.SPACING_3B};

	& > *:first-child {
		flex: 1;
	}
`

const LoaderWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: ${Spacings.SPACING_6B} 0;
`

const EmptyState = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: ${Spacings.SPACING_6B} 0;
	color: ${({ theme }) => theme.colors.text.secondary};
	font-size: ${Text.SMALL};
`

const SubscriptionCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_4B};
	margin-bottom: ${Spacings.SPACING_3B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
`

const CardHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
`

const PlanName = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
	text-transform: capitalize;
`

const DetailRow = styled.div`
	display: flex;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	font-size: ${Text.SMALL};
`

const DetailLabel = styled.span`
	color: ${({ theme }) => theme.colors.text.secondary};
`

const DetailValue = styled.span`
	color: ${({ theme }) => theme.colors.text.primary};
	font-weight: ${Text.BOLD_500};
`

const CardFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${Spacings.SPACING_2B};
`

// Ensure exactly one primary email: keep the flagged one, else make the first primary.
const normalizeEmails = (list) => {
	const emails = (list ?? []).map((e) => ({ email_id: e.email_id, primary: !!e.primary }))
	if (emails.length && !emails.some((e) => e.primary)) {
		emails[0].primary = true
	}
	return emails
}

const UserSettings = ({ data, update }) => {
	const dispatch = useDispatch()
	const user = useSelector((_s) => _s.getIn(['user', 'user']))
	const [tenderExport, setTenderExport] = useState(() => ({
		has_permission: data?.tender_export_info?.has_permission ?? false,
		max_count: data?.tender_export_info?.max_count ?? DEFAULT_MAX_COUNT,
	}))
	const [tenderResultExport, setTenderResultExport] = useState(() => ({
		has_permission: data?.tender_result_export_info?.has_permission ?? false,
		max_count: data?.tender_result_export_info?.max_count ?? DEFAULT_MAX_COUNT,
	}))
	const [isCompanyProfileView, setIsCompanyProfileView] = useState(data?.is_company_profile_view_subscribed ?? false)
	const [aiSynopsis, setAiSynopsis] = useState(() => ({
		enabled: data?.ai_access?.enabled ?? false,
		search_limit: data?.ai_access?.search_limit ?? DEFAULT_SEARCH_LIMIT,
	}))

	const [emails, setEmails] = useState(() => normalizeEmails(data?.emails))
	const [newEmail, setNewEmail] = useState('')
	const [emailError, setEmailError] = useState('')

	const [subscriptions, setSubscriptions] = useState([])
	const [subsLoading, setSubsLoading] = useState(false)

	// Emails are optional — only a non-empty list has to carry a primary.
	const canSubmit = useMemo(() => emails.length === 0 || emails.some((e) => e.primary), [emails])

	const fetchSubscriptions = () => {
		if (!data?.user_id) return
		setSubsLoading(true)
		getUserSubscriptions(data.user_id)
			.then((response) => {
				// Endpoint may return an array directly or wrap it under a common key.
				const list = Array.isArray(response) ? response : response?.data ?? response?.subscriptions ?? []
				setSubscriptions(list)
			})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message ?? 'Failed to load subscriptions', { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				setSubsLoading(false)
			})
	}

	useEffect(() => {
		fetchSubscriptions()
	}, [data?.user_id])

	const cancelSubscription = (subscription) => {
		const subscriptionId = subscription?.subscription_id ?? subscription?.id
		dispatch(showLoader(true))
		cancelUserSubscription(subscriptionId, data?.user_id)
			.then(() => {
				dispatch(showToast(true, 'Subscription cancelled', { type: TOAST_TYPE.SUCCESS }))
				update?.()
				fetchSubscriptions()
			})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message ?? 'Failed to cancel subscription', { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const confirmCancel = (subscription) => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: `Cancel the ${subscription?.plan_name || ''} subscription for ${subscription?.region || 'this region'}? This can't be undone.`,
				confirmText: strings('confirm'),
				confirmAction: (confirmed) => {
					dispatch(toggleModal(false))
					if (confirmed) cancelSubscription(subscription)
				},
			})
		)
	}

	const toggleExport = (setter) => (e) => {
		const checked = e.target.checked
		setter((prev) => ({
			...prev,
			has_permission: checked,
			// Restore the default row limit when enabling with an empty value.
			max_count: checked && !prev.max_count ? DEFAULT_MAX_COUNT : prev.max_count,
		}))
	}

	const changeExportCount = (setter) => (e) => {
		const value = e.target.value
		setter((prev) => ({ ...prev, max_count: value }))
	}

	const toggleAiSynopsis = (e) => {
		const checked = e.target.checked
		setAiSynopsis((prev) => ({
			...prev,
			enabled: checked,
			// Restore the default search limit when enabling with an empty value.
			search_limit: checked && !prev.search_limit ? DEFAULT_SEARCH_LIMIT : prev.search_limit,
		}))
	}

	const changeSearchLimit = (e) => {
		const value = e.target.value
		setAiSynopsis((prev) => ({ ...prev, search_limit: value }))
	}

	const setPrimary = (index) => {
		setEmails((list) => list.map((e, i) => ({ ...e, primary: i === index })))
	}

	const removeEmail = (index) => {
		setEmails((list) => {
			// At least one email is required and the primary email can't be removed.
			if (list.length <= 1 || list[index].primary) return list
			return list.filter((_, i) => i !== index)
		})
	}

	const addEmail = () => {
		const email = newEmail.trim()
		if (!EMAIL_REGEX.test(email)) {
			setEmailError(strings('enter_valid_email'))
			return
		}
		if (emails.some((e) => e.email_id.toLowerCase() === email.toLowerCase())) {
			setEmailError('Email already added')
			return
		}
		if (emails.length >= MAX_EMAILS) return

		// The very first email is automatically the primary one.
		setEmails((list) => [...list, { email_id: email, primary: list.length === 0 }])
		setNewEmail('')
		setEmailError('')
	}

	const submit = () => {
		const payload = {
			company_name: data?.company_name ?? '',
			emails: emails.map((e) => ({ email_id: e.email_id, primary: e.primary })),
			is_company_profile_view_subscribed: isCompanyProfileView,
			tender_export_info: {
				has_permission: tenderExport.has_permission,
				max_count: Number(tenderExport.max_count) || 0,
			},
			tender_result_export_info: {
				has_permission: tenderResultExport.has_permission,
				max_count: Number(tenderResultExport.max_count) || 0,
			},
			ai_access: {
				enabled: aiSynopsis.enabled,
				search_limit: Number(aiSynopsis.search_limit) || 0,
			},
		}

		dispatch(showLoader(true))
		editTenderUser(payload, data?.user_id, user?.mobile_number)
			.then(() => {
				dispatch(showToast(true, 'Settings updated', { type: TOAST_TYPE.SUCCESS }))
				update?.()
				dispatch(toggleDrawer(false))
			})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message ?? 'Failed to update settings', { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	// Ask for confirmation before persisting — the backend overwrites all user settings on submit.
	const submitHandler = () => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: `Update settings for ${data?.company_name || data?.mobile_no || 'this user'}?`,
				confirmText: strings('submit'),
				confirmAction: (confirmed) => {
					dispatch(toggleModal(false))
					if (confirmed) submit()
				},
			})
		)
	}

	const renderSubscription = (subscription, index) => (
		<SubscriptionCard key={subscription?.subscription_id ?? index}>
			<CardHeader>
				<PlanName>{subscription?.plan_name || '-'}</PlanName>
			</CardHeader>
			<DetailRow>
				<DetailLabel>{strings('region')}</DetailLabel>
				<DetailValue>{subscription?.region || '-'}</DetailValue>
			</DetailRow>
			<DetailRow>
				<DetailLabel>{strings('start', 'date')}</DetailLabel>
				<DetailValue>{formatDate(subscription?.start_date)}</DetailValue>
			</DetailRow>
			<DetailRow>
				<DetailLabel>{strings('expiry', 'date')}</DetailLabel>
				<DetailValue>{formatDate(subscription?.expiry_date)}</DetailValue>
			</DetailRow>
			<CardFooter>
				<Button size='small' color='danger' variant='outline' onClick={() => confirmCancel(subscription)}>
					Cancel subscription
				</Button>
			</CardFooter>
		</SubscriptionCard>
	)

	const renderExportSetting = (idPrefix, label, setting, setter) => (
		<Section>
			<Checkbox id={`${idPrefix}_permission`} label={label} checked={setting.has_permission} onChange={toggleExport(setter)} />
			{setting.has_permission && (
				<CountWrapper>
					<Input
						id={`${idPrefix}_max_count`}
						label='Rows limit in CSV'
						type='number'
						min={1}
						value={setting.max_count}
						onChange={changeExportCount(setter)}
					/>
				</CountWrapper>
			)}
		</Section>
	)

	return (
		<>
			<DrawerBodyWrapper>
				{renderExportSetting('tender_export', 'Tender export', tenderExport, setTenderExport)}
				{renderExportSetting('tender_result_export', 'Tender Result export', tenderResultExport, setTenderResultExport)}

				<Section>
					<Checkbox
						id='company_profile_view'
						label='Allow view company profile'
						checked={isCompanyProfileView}
						onChange={(e) => setIsCompanyProfileView(e.target.checked)}
					/>
				</Section>

				<Section>
					<SectionTitle>AI Synopsis</SectionTitle>
					<InlineRow>
						<Checkbox id='ai_synopsis_enabled' label='Enable AI synopsis' checked={aiSynopsis.enabled} onChange={toggleAiSynopsis} />
						{aiSynopsis.enabled && (
							<LimitWrapper>
								<Input
									id='ai_synopsis_search_limit'
									label='Max limit'
									type='number'
									min={1}
									value={aiSynopsis.search_limit}
									onChange={changeSearchLimit}
								/>
							</LimitWrapper>
						)}
					</InlineRow>
				</Section>

				<Section>
					<SectionTitle>Emails</SectionTitle>
					{emails.map((email, index) => (
						<EmailRow key={email.email_id}>
							<EmailText title={email.email_id}>{email.email_id}</EmailText>
							{email.primary ? (
								<PrimaryTag>Primary</PrimaryTag>
							) : (
								<Button variant='link' size='small' onClick={() => setPrimary(index)}>
									Set as primary
								</Button>
							)}
							<IconButton
								type='button'
								disabled={email.primary || emails.length <= 1}
								title={email.primary ? "Primary email can't be removed" : 'Remove email'}
								onClick={() => removeEmail(index)}
							>
								<Close />
							</IconButton>
						</EmailRow>
					))}

					{emails.length < MAX_EMAILS && (
						<AddEmailRow>
							<Input
								id='new_email'
								placeholder='Add email'
								type='email'
								value={newEmail}
								error={emailError || undefined}
								onChange={(e) => {
									setNewEmail(e.target.value)
									if (emailError) setEmailError('')
								}}
							/>
							<Button variant='outline' onClick={addEmail} disabled={!newEmail.trim()}>
								Add
							</Button>
						</AddEmailRow>
					)}
				</Section>

				<Section>
					<SectionTitle>Subscriptions</SectionTitle>
					{subsLoading && (
						<LoaderWrapper>
							<Loader />
						</LoaderWrapper>
					)}
					{!subsLoading && subscriptions.length === 0 && <EmptyState>No subscriptions found</EmptyState>}
					{!subsLoading && subscriptions.map(renderSubscription)}
				</Section>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button onClick={submitHandler} disabled={!canSubmit}>
					{strings('submit')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default UserSettings
